import type { ValidationResult } from '$lib/forms';

import type { ReadonlyNetwork } from './config-item';

/**
 * The heartbeat window is stored in the raw config as a string suffixed with "s", and the heartbeat input type in the machine config is a number.
 * This function converts the raw config heartbeat window value to a number for the machine config.
 * @param heartbeatWindow string representing the heartbeat window number. should be suffixed with "s", for seconds (ex. "2s")
 * @returns a number representing the number of seconds in the heartbeat window
 */
export const heartbeatWindowStringToNumber = (
  heartbeatWindow: string
): number => {
  return Number(heartbeatWindow.replace('s', ''));
};

/**
 * The heartbeat window is stored in the machine config as a number (in seconds).
 * This function converts the machine config number value to the raw config string value.
 * @param heartbeatWindowSeconds number representing the number of seconds
 * @returns a string value for heartbeat window
 */
export const heartbeatWindowNumberToString = (
  heartbeatWindowSeconds: number
): string => {
  return `${heartbeatWindowSeconds}s`;
};

/**
 * Validates a network. For use with createFormState<Config.ReadonlyNetwork>.
 */
export const validateNetwork = (
  network: ReadonlyNetwork | undefined
): ValidationResult<ReadonlyNetwork> => {
  let validationErrors: ValidationResult<ReadonlyNetwork>['errors'];
  if (network === undefined) {
    return { values: network, errors: undefined };
  }

  if (network.heartbeatWindowS !== undefined && network.heartbeatWindowS <= 0) {
    validationErrors = {
      ...validationErrors,
      heartbeatWindowS: 'Heartbeat window must be a number greater than 0.',
    };
  }

  if (!network.bindAddress) {
    return { values: network, errors: validationErrors };
  }

  const [host, port] = network.bindAddress.split(':') as [string, string];
  if (port === '' && host !== '') {
    validationErrors = {
      ...validationErrors,
      bindAddress: 'Port cannot be empty.',
    };
  }

  return { values: network, errors: validationErrors };
};

export const createBindAddress = (
  updatedHost: string | undefined,
  updatedPort: string | undefined
): string | undefined => {
  if (
    (updatedHost === undefined || updatedHost === '') &&
    (updatedPort === undefined || updatedPort === '')
  ) {
    return undefined;
  }
  return `${updatedHost ?? ''}:${updatedPort ?? ''}`;
};
